import React from 'react';
import { Conversation, UserProfile } from '../types';
import { Compass, Radio, Users, Sparkles, Check, Plus } from 'lucide-react';

interface DiscoverViewProps {
  currentUser: UserProfile;
  channels: Conversation[];
  onJoinChannel: (channel: Conversation) => void;
  onOpenCreateChannel: () => void;
  onSelectConversation: (convId: string) => void;
}

export const DiscoverView: React.FC<DiscoverViewProps> = ({
  currentUser,
  channels,
  onJoinChannel,
  onOpenCreateChannel,
  onSelectConversation,
}) => {
  const publicChannels = [
    {
      id: 'pub_tech',
      title: 'وتين تك 📡',
      description: 'أحدث التطورات التقنية وأخبار الذكاء الاصطناعي والحوسبة السحابية.',
      subscribers: 24800,
      avatar: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&auto=format&fit=crop&q=80',
      category: 'تقنية وابتكار',
    },
    {
      id: 'pub_vibes',
      title: 'همسات وتين ✨',
      description: 'اقتباسات يومية راقية، أفكار إلهامية، وجمال الكلمة العربية الأصيلة.',
      subscribers: 41200,
      avatar: 'https://images.unsplash.com/photo-1516962215378-7fa2e137ae93?w=400&auto=format&fit=crop&q=80',
      category: 'أدب وإلهام',
    },
    {
      id: 'pub_design',
      title: 'تصميم ومستقبل الواجهات 🎨',
      description: 'ملتقى محترفي التصميم UI/UX، الموشن جرافيك، والجماليات الرقمية.',
      subscribers: 18500,
      avatar: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=400&auto=format&fit=crop&q=80',
      category: 'تصميم وفنون',
    },
    {
      id: 'pub_business',
      title: 'رواد أعمال وتين 💼',
      description: 'أفكار المشاريع الناشئة، التمويل الجريء، واستراتيجيات النمو القيادي.',
      subscribers: 12900,
      avatar: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&auto=format&fit=crop&q=80',
      category: 'أعمال واستثمار',
    },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-[#060911] font-['Cairo']">
      <div className="max-w-4xl mx-auto mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black text-white flex items-center gap-2">
            <span>استكشف مجتمع وتين</span>
            <Compass className="w-5 h-5 text-emerald-400" />
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            اكتشف أبرز القنوات الموثوقة والمجتمعات الصاعدة وتفاعل معها
          </p>
        </div>

        <button
          onClick={onOpenCreateChannel}
          className="px-4 py-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-1.5 transition active:scale-95 shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>إنشاء قناة أو مجموعة</span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        <h3 className="text-xs font-bold text-slate-400">القنوات الأكثر تفاعلاً اليوم</h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {publicChannels.map((item) => (
            <div
              key={item.id}
              className="p-4 rounded-3xl bg-[#0e1422] border border-slate-800/80 hover:border-emerald-500/40 transition flex flex-col justify-between group shadow-lg"
            >
              <div className="flex items-start gap-3.5 mb-3">
                <img
                  src={item.avatar}
                  alt={item.title}
                  className="w-14 h-14 rounded-2xl object-cover ring-1 ring-slate-700 shrink-0 group-hover:scale-105 transition"
                />
                <div className="min-w-0">
                  <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full inline-block mb-1">
                    {item.category}
                  </span>
                  <h4 className="text-xs font-bold text-white group-hover:text-emerald-300 transition truncate">
                    {item.title}
                  </h4>
                  <p className="text-[11px] text-slate-400 line-clamp-2 mt-1 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-800/60 flex items-center justify-between">
                <div className="flex items-center gap-1 text-[10px] text-slate-400 font-mono">
                  <Users className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{item.subscribers.toLocaleString('ar-SA')} مشترك</span>
                </div>

                <button
                  onClick={() => {
                    const match = channels.find((c) => c.title.includes(item.title.split(' ')[0]));
                    if (match) {
                      onSelectConversation(match.id);
                    } else {
                      onJoinChannel({
                        id: `conv_${item.id}`,
                        type: 'channel',
                        title: item.title,
                        avatar: item.avatar,
                        description: item.description,
                        participants: [currentUser.username],
                        createdAt: Date.now(),
                      });
                    }
                  }}
                  className="px-3 py-1.5 rounded-xl bg-[#141b27] hover:bg-emerald-500 hover:text-[#080d16] text-emerald-300 text-xs font-bold border border-emerald-500/30 transition flex items-center gap-1"
                >
                  <Radio className="w-3.5 h-3.5" />
                  <span>انضمام ومتابعة</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
